import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ActionIcon, Button, ComboboxItem, Menu, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCheck,
  IconClipboardData,
  IconExternalLink,
  IconPencil,
  IconX,
} from "@tabler/icons-react";

import { CancelAppointmentModal } from "@components/CancelAppointmentModal";
import { LastUpdatedTimestamp } from "@components/LastUpdated";
import { PatientNotesModal } from "@components/PatientNotesModal";
import { PermissionGate } from "@components/PermissionGate";
import { RejectAppointmentModal } from "@components/RejectAppointmentModal";

import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";

import { useAcceptAppointment } from "@hooks/mutations/appointment/useAcceptAppointment";
import { useUpdateAppointment } from "@hooks/mutations/appointment/useUpdateAppointment";
import { useCreateConsultationWithAppointment } from "@hooks/mutations/consultation/useCreateConsultationWithAppointment";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { Appointment, AppointmentCategory } from "@customTypes/appointment";
import { BasicUser } from "@customTypes/user";

import { EditHomecareAppointmentModal } from "../EditHomecareAppointmentModal";
import { EditLabAppointmentModal } from "../EditLabAppointmentModal";
import { EditTeleconsultationAppointmentModal } from "../EditTeleconsultationAppointmentModal";

import { StyledTableControls } from "./TableControls.styled";


interface TableControlsProps {
  hasAcceptBtn?: boolean;
  row: Appointment;
  variant: "incoming" | "booked";
  doctors: ComboboxItem[];
  appointmentType: AppointmentCategory;
  isUpdateAppointmentLoading?: boolean;
}

export const TableControls = ({
  hasAcceptBtn,
  row,
  variant,
  doctors,
  appointmentType,
  isUpdateAppointmentLoading,
}: TableControlsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [
    editHomecareModalOpened,
    { open: editHomecareModalOpen, close: editHomecareModalClose },
  ] = useDisclosure(false);
  const [
    editTeleconsultationModalOpened,
    {
      open: editTeleconsultationModalOpen,
      close: editTeleconsultationModalClose,
    },
  ] = useDisclosure(false);
  const [
    editLabApptModalOpened,
    { open: editLabApptModalOpen, close: editLabApptModalClose },
  ] = useDisclosure(false);
  const [
    patientNotesModalOpened,
    { open: openPatientNotesModal, close: closePatientNotesModal },
  ] = useDisclosure(false);
  const [
    cancelModalOpened,
    { open: openCancelModal, close: closeCancelModal },
  ] = useDisclosure(false);
  const [
    rejectModalOpened,
    { open: openRejectModal, close: closeRejectModal },
  ] = useDisclosure(false);

  const {
    mutate: createConsultationWithAppointment,
    isLoading: isCreateConsultationWithAppointmentLoading,
  } = useCreateConsultationWithAppointment();
  const { mutate: acceptAppointment, isLoading: isAcceptAppointmentLoading } =
    useAcceptAppointment();
  const handleErrors = useQueryErrorHandling();

  return (
    <>
      <StyledTableControls className="table-controls">
        <EditHomecareAppointmentModal
          opened={editHomecareModalOpened}
          close={editHomecareModalClose}
          selectedRow={row}
          doctors={doctors}
          variant={variant}
        />
        <EditTeleconsultationAppointmentModal
          opened={editTeleconsultationModalOpened}
          close={editTeleconsultationModalClose}
          selectedRow={row}
          doctors={doctors}
          variant={variant}
        />
        <EditLabAppointmentModal
          opened={editLabApptModalOpened}
          close={editLabApptModalClose}
          selectedRow={row}
          doctors={doctors}
          variant={variant}
        />
        <PatientNotesModal
          opened={patientNotesModalOpened}
          close={closePatientNotesModal}
          patientId={row.patientId}
        />
        <CancelAppointmentModal
          opened={cancelModalOpened}
          close={closeCancelModal}
          appointmentId={row.id}
        />
        <RejectAppointmentModal
          opened={rejectModalOpened}
          close={closeRejectModal}
          appointmentId={row.id}
        />

        {variant === "booked" ? (
          <Menu withArrow position="bottom-end" withinPortal>
            <Menu.Target>
              <Button
                variant="outline"
                size="compact-sm"
                disabled={isUpdateAppointmentLoading}>
                {t("action")}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <PermissionGate permission="appointments.edit"><Menu.Item
                leftSection={<IconPencil size={16} stroke={1.5} />}
                onClick={() => {
                  if (appointmentType === "teleconsultation") {
                    editTeleconsultationModalOpen();
                  } else if (appointmentType === "homecare") {
                    editHomecareModalOpen();
                  }
                }}>
                {t("edit-appointment")}
              </Menu.Item></PermissionGate>

              <Menu.Item
                leftSection={<IconClipboardData size={16} stroke={1.5} />}
                onClick={openPatientNotesModal}>
                {t("open-patient-notes")}
              </Menu.Item>

              <Menu.Item
                leftSection={<IconExternalLink size={16} stroke={1.5} />}
                // color={!row.consultationId ? "red" : "primary"}
                onClick={() => {
                  if (row.consultationId) {
                    navigate(
                      `/patients/${row.patientId}/consultation/general/${row.consultationId}`
                    );
                  } else {
                    const id = showLoadingNotification({});
                    createConsultationWithAppointment(
                      {
                        appointmentId: row.id,
                        patientId: row.patientId,
                      },
                      {
                        onError: res => handleErrors(res, "mutation", id),
                        onSuccess: res => {
                          showSuccessNotification({
                            id,
                            title: "success",
                            message: "create-consultation-success",
                          });
                          setTimeout(() => {
                            navigate(
                              `/patients/${row.patientId}/consultation/general/${res.data.consultation_id}`
                            );
                          }, 500);
                        },
                      }
                    );
                  }
                }}>
                {!row.consultationId
                  ? t("create-consultation")
                  : t("view-consultation")}
              </Menu.Item>

              <Menu.Divider />

              <Menu.Item
                leftSection={<IconX size={16} stroke={1.5} />}
                color="red"
                onClick={openCancelModal}>
                {t("cancel-appointment")}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : (
          <>
            <Tooltip
              label={t("edit")}
              withArrow
              events={{ hover: true, focus: true, touch: true }}>
              <PermissionGate permission="appointments.edit"><ActionIcon
                variant="outline"
                disabled={
                  isUpdateAppointmentLoading ||
                  isAcceptAppointmentLoading
                }
                size="md"
                onClick={() => {
                  if (appointmentType === "teleconsultation") {
                    editTeleconsultationModalOpen();
                  } else if (appointmentType === "homecare") {
                    editHomecareModalOpen();
                  } else if (appointmentType === "lab") {
                    editLabApptModalOpen();
                  }
                }}>
                <IconPencil size={16} />
              </ActionIcon></PermissionGate>
            </Tooltip>

            {hasAcceptBtn ? (
              <Tooltip
                label={t("accept")}
                withArrow
                events={{ hover: true, focus: true, touch: true }}>
                <PermissionGate permission="appointments.assign"><ActionIcon
                  variant="outline"
                  disabled={
                    !(row.doctorAssigned as BasicUser)?.id ||
                    isUpdateAppointmentLoading
                  }
                  loading={isAcceptAppointmentLoading}
                  onClick={() => {
                    // console.log(row.doctorAssigned);
                    const id = showLoadingNotification({});

                    acceptAppointment(
                      {
                        appointmentId: row?.id,
                      },
                      {
                        onSuccess: () => {
                          showSuccessNotification({
                            id,
                            title: "success",
                            message: "book-appointment-success",
                          });
                        },
                        onError: (res: any) => {
                          handleErrors(res, "mutation", id);
                        },
                      }
                    );
                  }}>
                  <IconCheck size={16} />
                </ActionIcon></PermissionGate>
              </Tooltip>
            ) : null}

            <Tooltip
              label={t("reject")}
              withArrow
              events={{ hover: true, focus: true, touch: true }}>
              <PermissionGate permission="appointments.assign"><ActionIcon
                variant="outline"
                color="red"
                disabled={
                  isUpdateAppointmentLoading || isAcceptAppointmentLoading
                }
                onClick={openRejectModal}>
                <IconX size={16} />
              </ActionIcon></PermissionGate>
            </Tooltip>
          </>
        )}
      </StyledTableControls>
      <LastUpdatedTimestamp
        updatedDate={row?.updatedAt as Date}
        className="last-updated"
      />
    </>
  );
};
